/**
 * Risk Matrix: are the miles going to the right drivers?
 *
 * Two numbers per driver, both from the scorecard:
 *
 *   x  their COMBINED SCORE    (compiled_score -- higher is better)
 *   y  the MILES they drove
 *
 * A scorecard tells you who drives well. It cannot tell you how much road that
 * record is covering, because SCHEDULING decides that, not scoring. Every
 * driver on the roster can look acceptable and the company can still be
 * carrying more risk than it needs to, purely through who got the work.
 *
 * ERM events are deliberately NOT used here (2026-08-25, ben's call). An
 * earlier version measured risk as the at-fault event rate from the ERM; the
 * join and the endpoint behind it still exist (utils/erm_charts.js,
 * /bookings/ops-desk/driver-outcomes) and nothing about them was removed, so
 * putting that axis back is a change of inputs rather than a rebuild.
 *
 * Which score: `compiled_score`, the weighted composite the product labels
 * "Combined Score" -- the same number the tier chips and the roster ranking
 * key on, so this page agrees with the rest of Driver Manager rather than
 * introducing a second definition of "good driver". `risk_score` ("Safety
 * Score", the raw telematics figure) is the other candidate; switching is a
 * one-line change to SCORE_FIELD below plus the axis label on the page.
 */

// the scorecard field this page scores on; see the note above
export const SCORE_FIELD = 'compiled_score'

export const OVER_DISPATCHED = 'over'
export const UNDER_DISPATCHED = 'under'
export const IN_LINE = 'inline'

// a driver is only called out once they are meaningfully off their mark -- a
// 12% band either side, so ordinary scheduling noise is not a finding
export const OVER_THRESHOLD = 1.12
export const UNDER_THRESHOLD = 0.88

// no driver's mark may run past double or below zero of their own mileage,
// however extreme their score
export const MAX_RATIO = 2

const INSUFFICIENT_DATA_TIER = 'Insufficient Data'

export const EXPOSURE_OVER_RISK = 'Over-dispatched risk'
export const EXPOSURE_WORKHORSE = 'Workhorses'
export const EXPOSURE_LOW_LOW = 'Low miles, high score'
export const EXPOSURE_UNDERUSED = 'Low score, light load'

// the quadrant that needs action first, then its release valve, then the two
// that need nothing today
export const EXPOSURE_QUADRANT_ORDER = [
  EXPOSURE_OVER_RISK,
  EXPOSURE_WORKHORSE,
  EXPOSURE_UNDERUSED,
  EXPOSURE_LOW_LOW,
]

export const EXPOSURE_QUADRANT_BLURB = {
  [EXPOSURE_OVER_RISK]:
    'Below the fleet’s average combined score and above average on miles. The heaviest work is going to the weakest records — this is the quadrant the page exists for.',
  [EXPOSURE_WORKHORSE]:
    'Strong combined score, heavy miles. Dispatch is already doing the right thing with these drivers.',
  [EXPOSURE_UNDERUSED]:
    'A weak combined score, but not many miles behind it. Worth coaching; not yet worth re-scheduling.',
  [EXPOSURE_LOW_LOW]:
    'Strong score, light load. This is the release valve — the drivers with room on the calendar.',
}

export const EXPOSURE_QUADRANT_COLORS = {
  [EXPOSURE_OVER_RISK]: { light: '#C1352F', dark: '#E4736C' },
  [EXPOSURE_WORKHORSE]: { light: '#1E7B3C', dark: '#4FB477' },
  [EXPOSURE_UNDERUSED]: { light: '#B07D08', dark: '#D6A431' },
  [EXPOSURE_LOW_LOW]: { light: '#2F6690', dark: '#6BA3CE' },
}

// Corner labels. x is the combined score ascending left to right, so HIGHER is
// safer and the right-hand side is the good side -- which puts the best
// drivers (strong score, heavy miles) in the upper right, where a reader
// expects "good" to be. That falls out of the metric rather than needing a
// reversed axis, which an earlier at-fault-rate version did need.
export const EXPOSURE_CORNER_LABELS = {
  topLeft: 'OVER-DISPATCHED RISK',
  topRight: 'WORKHORSES',
  bottomLeft: 'LOW SCORE, LIGHT LOAD',
  bottomRight: 'LOW MILES, HIGH SCORE',
}

export const EXPOSURE_CORNER_QUADRANTS = {
  topLeft: EXPOSURE_OVER_RISK,
  topRight: EXPOSURE_WORKHORSE,
  bottomLeft: EXPOSURE_UNDERUSED,
  bottomRight: EXPOSURE_LOW_LOW,
}

export function exposureQuadrantColor(quadrant, midnight) {
  const pair = EXPOSURE_QUADRANT_COLORS[quadrant]
  if (!pair) return '#adb5bd'
  return midnight ? pair.dark : pair.light
}

function round(n, places = 1) {
  const f = Math.pow(10, places)
  return Math.round(n * f) / f
}

/**
 * How this driver's own mileage should scale, given how their combined score
 * compares to the typical driver's.
 *
 *      ratio = score / average score
 *
 * A driver exactly at the fleet average gets 1.0 -- their mark sits on their
 * own bar. A better-than-average score pushes the mark right (they could carry
 * more); a worse one pulls it left. Capped at MAX_RATIO so one outstanding
 * score cannot suggest an unbounded workload.
 *
 * Scaling each driver's OWN mileage, rather than redistributing the fleet's
 * total across the roster, is what keeps a part-timer comparable to a
 * full-timer without an employment-status roster (CM tenants have none). A
 * redistributing version was measured against Anderson and found to be mostly
 * detecting "drives a lot" rather than "drives badly": 97% of the top half by
 * mileage sat past their mark against 20% of the bottom half. Total miles are
 * deliberately NOT conserved here -- this is a per-driver read, not a plan.
 */
export function mileageRatio(score, avgScore) {
  const a = Number(avgScore) || 0
  if (a <= 0) return 1
  return Math.min(MAX_RATIO, Math.max(0, (Number(score) || 0) / a))
}

/**
 * Over-dispatched: meaningfully past the mark, which can only happen when the
 * driver scores below the fleet average. The score argument is kept explicit
 * so the intent stays legible at the call site and a future change to the mark
 * cannot silently start flagging strong drivers for being busy.
 */
export function dispatchStatus(miles, mark, score, avgScore) {
  if (!mark) return IN_LINE
  const weak = (Number(score) || 0) < (Number(avgScore) || 0)
  if (weak && miles > mark * OVER_THRESHOLD) return OVER_DISPATCHED
  if (!weak && miles < mark * UNDER_THRESHOLD) return UNDER_DISPATCHED
  return IN_LINE
}

/**
 * Build everything the Risk Matrix renders.
 *
 * @param drivers  scorecard rows (driver_id, driver_name, total_miles, risk_score, tier)
 * @returns {{ rows, fleet, quadrants, coverage }}
 */
export function buildExposure({ drivers } = {}) {
  const placed = []
  const unscored = []

  ;(drivers || []).forEach(row => {
    if (!row) return
    const miles = Number(row.total_miles) || 0
    // guard null/undefined/'' explicitly: Number(null) is 0 and isFinite(0) is
    // true, so a bare Number() would plot an UNSCORED driver at zero -- the
    // worst quadrant, on no evidence at all
    const raw = row[SCORE_FIELD]
    const score = raw === null || raw === undefined || raw === '' ? NaN : Number(raw)
    // Insufficient Data drivers are excluded for the same reason the rest of
    // Driver Manager leaves them out of rankings: too little activity to score
    // fairly. A driver with no score at all is listed as unscored rather than
    // plotted at zero, which would park them in the worst quadrant on no
    // evidence.
    if (row.compiled_score_tier === INSUFFICIENT_DATA_TIER) return
    if (miles <= 0) return
    if (!Number.isFinite(score)) { unscored.push(row); return }

    placed.push({
      driver_id: String(row.driver_id),
      driver_name: row.driver_name,
      tier: row.compiled_score_tier,
      score: round(score, 1),
      miles,
    })
  })

  const totalMiles = placed.reduce((s, d) => s + d.miles, 0)

  // The headline comparison. One driver one vote, versus weighted by the miles
  // each driver's score actually rides on. When the mile-weighted score is
  // HIGHER, the road is in better hands than the roster alone suggests -- and
  // no individual scorecard can show that, because it is a property of the
  // schedule rather than of any driver.
  const simpleAvgScore = placed.length
    ? placed.reduce((s, d) => s + d.score, 0) / placed.length
    : 0
  const mileWeightedScore = totalMiles
    ? placed.reduce((s, d) => s + d.score * d.miles, 0) / totalMiles
    : 0

  placed.forEach(d => {
    d.ratio = round(mileageRatio(d.score, simpleAvgScore), 2)
    d.mark = d.miles * mileageRatio(d.score, simpleAvgScore)
    // kept under the old name too: the page, the drill-downs and the bars all
    // read `fairShare`, and renaming it buys nothing
    d.fairShare = d.mark
    d.delta = Math.round(d.miles - d.mark)
    d.sharePct = totalMiles ? round(d.miles / totalMiles * 100, 1) : 0
    d.status = dispatchStatus(d.miles, d.mark, d.score, simpleAvgScore)
  })

  const over = placed.filter(d => d.status === OVER_DISPATCHED).sort((a, b) => b.delta - a.delta)
  const under = placed.filter(d => d.status === UNDER_DISPATCHED).sort((a, b) => a.delta - b.delta)
  const excessMiles = Math.round(over.reduce((s, d) => s + (d.miles - d.mark), 0))

  // miles being driven by anyone below the fleet's average score, which is the
  // plainest statement of what is actually at stake
  const belowAvg = placed.filter(d => d.score < simpleAvgScore)
  const belowAvgMiles = Math.round(belowAvg.reduce((s, d) => s + d.miles, 0))
  const belowAvgShare = totalMiles ? round(belowAvgMiles / totalMiles * 100, 1) : 0

  // share of the fleet's miles run by the quarter with the weakest scores
  const byScore = placed.slice().sort((a, b) => a.score - b.score)
  const q = Math.max(1, Math.round(placed.length / 4))
  const worstQuartileMiles = byScore.slice(0, q).reduce((s, d) => s + d.miles, 0)
  const worstQuartileShare = totalMiles ? round(worstQuartileMiles / totalMiles * 100, 1) : 0

  const avgMiles = placed.length ? totalMiles / placed.length : 0
  placed.forEach(d => {
    const strong = d.score >= simpleAvgScore
    const busy = d.miles >= avgMiles
    d.quadrant = strong
      ? (busy ? EXPOSURE_WORKHORSE : EXPOSURE_LOW_LOW)
      : (busy ? EXPOSURE_OVER_RISK : EXPOSURE_UNDERUSED)
  })

  const quadrants = {}
  EXPOSURE_QUADRANT_ORDER.forEach(n => { quadrants[n] = [] })
  placed.forEach(d => { quadrants[d.quadrant].push(d) })
  EXPOSURE_QUADRANT_ORDER.forEach(n => {
    quadrants[n].sort((a, b) => b.delta - a.delta || b.miles - a.miles)
  })

  return {
    rows: placed.slice().sort((a, b) => b.delta - a.delta),
    quadrants,
    fleet: {
      drivers: placed.length,
      totalMiles: Math.round(totalMiles),
      simpleAvgScore: round(simpleAvgScore, 1),
      mileWeightedScore: round(mileWeightedScore, 1),
      // POSITIVE is good here: the miles are going to the stronger scores
      scoreGap: round(mileWeightedScore - simpleAvgScore, 1),
      worstQuartileShare,
      quartileSize: q,
      avgMiles: Math.round(avgMiles),
      overCount: over.length,
      underCount: under.length,
      excessMiles,
      belowAvgCount: belowAvg.length,
      belowAvgMiles,
      belowAvgShare,
      over: over.slice(0, 5),
      under: under.slice(0, 5),
    },
    coverage: {
      plotted: placed.length,
      unscored: unscored.length,
    },
  }
}

// Both axes are already spent -- score on x, miles on y -- and there is no
// honest third quantity left now that event counts are out, so every dot is
// the same size rather than encoding something arbitrary.
export const DOT_RADIUS = 6
export function exposureBubbleRadius() {
  return DOT_RADIUS
}
